
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowRight, CalendarClock, CheckSquare2, CircleDollarSign, FolderKanban,
  MapPin, Plus, Target, UserRoundPlus,
} from 'lucide-react'
import { api } from '../lib/apiClient'
import { formatDate, formatMoney, formatPercent, formatTime, humanize, isOverdue } from '../lib/formatters'
import type { AppRecord, CalendarEvent } from '../types'
import { useApi } from '../hooks/useApi'
import { ErrorState, LoadingState } from '../components/States'
import { StatusBadge } from '../components/StatusBadge'
import { activeOpportunityStatuses, aggregateCurrentOpportunityRevenue, aggregateOpportunityRevenue, isOpportunityFinanciallyReportable, opportunityRevenueLevels } from '../lib/opportunityRevenue'

type DashboardData = {
  leads: AppRecord[]; opportunities: AppRecord[]; quotes: AppRecord[]; projects: AppRecord[]
  tasks: AppRecord[]; issues: AppRecord[]; appointments: AppRecord[]; events: CalendarEvent[]
}

async function loadDashboard(): Promise<DashboardData> {
  const start = new Date()
  const end = new Date(start.getTime() + 1000 * 60 * 60 * 24 * 14)
  const [leads, opportunities, quotes, projects, tasks, issues, appointments, events] = await Promise.all([
    api.list<AppRecord>('leads'), api.list<AppRecord>('opportunities'), api.list<AppRecord>('quotes'), api.list<AppRecord>('projects'),
    api.list<AppRecord>('tasks'), api.list<AppRecord>('issues'), api.list<AppRecord>('appointments'),
    api.calendar<CalendarEvent>(start.toISOString(), end.toISOString()).catch(() => []),
  ])
  return { leads, opportunities, quotes, projects, tasks, issues, appointments, events }
}

const activeProjectStatuses = new Set(['pending_deposit', 'supplier_order_pending', 'fabrication', 'installation_scheduled', 'installed', 'after_sales'])



export function DashboardPage() {
  const { data, loading, error, reload } = useApi(loadDashboard, [])
  const today = new Date()
  const dateLabel = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).format(today)

  if (loading) return <div className="card"><LoadingState label="Preparing your workspace…" /></div>
  if (error || !data) return <div className="card"><ErrorState message={error || 'Dashboard data is unavailable.'} onRetry={reload} /></div>

  const openLeads = data.leads.filter((record) => ['new', 'contacted', 'qualified'].includes(String(record.status)))
  const activeOpportunities = data.opportunities.filter((record) => activeOpportunityStatuses.has(String(record.status)))
  const financialOpportunities = data.opportunities.filter(isOpportunityFinanciallyReportable)
  const pendingQuotes = data.quotes.filter((record) => record.quoteType === 'customer' && ['draft', 'sent'].includes(String(record.status)))
  const activeProjects = data.projects.filter((record) => activeProjectStatuses.has(String(record.status)))
  const overdueTasks = data.tasks.filter((record) => record.status === 'open' && isOverdue(record.dueDate))
  const openIssues = data.issues.filter((record) => ['open', 'in_progress', 'waiting_for_supplier'].includes(String(record.status)))

  const metrics = [
    { label: 'Open leads', value: openLeads.length, note: `${openLeads.filter((record) => record.status === 'new').length} new enquiries`, icon: UserRoundPlus, colour: 'bg-blue-50 text-blue-700', to: '/leads' },
    { label: 'Active opportunities', value: activeOpportunities.length, note: 'Open commercial stages', icon: Target, colour: 'bg-violet-50 text-violet-700', to: '/opportunities' },
    { label: 'Quotes in play', value: pendingQuotes.length, note: `${pendingQuotes.filter((record) => record.status === 'sent').length} awaiting decision`, icon: CircleDollarSign, colour: 'bg-amber-50 text-amber-700', to: '/quotes' },
    { label: 'Active projects', value: activeProjects.length, note: `${activeProjects.filter((record) => record.status === 'installation_scheduled').length} installs scheduled`, icon: FolderKanban, colour: 'bg-emerald-50 text-emerald-700', to: '/projects' },
  ]

  const focusTasks = data.tasks
    .filter((record) => record.status === 'open')
    .sort((a, b) => new Date(String(a.dueDate || '2999')).getTime() - new Date(String(b.dueDate || '2999')).getTime())
    .slice(0, 5)
  const upcomingAppointments = data.appointments
    .filter((record) => record.start && new Date(String(record.start)) >= today)
    .map((record) => ({ id: record.id, title: String(record.title || 'Appointment'), start: String(record.start), location: String(record.location || ''), type: String(record.type || 'appointment'), source: 'appointment' }))
  const calendarEvents = data.events.map((event) => ({ id: event.id, title: event.title, start: event.start, location: event.location, type: event.allDay ? 'all_day' : 'calendar', source: 'calendar' }))
  const schedule = [...upcomingAppointments, ...calendarEvents].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()).slice(0, 5)

  const pipelineStages = [
    ['New', 'new'], ['Visit scheduled', 'visit_scheduled'], ['Measured', 'measured'], ['Quote prep', 'quote_in_preparation'], ['Quote sent', 'quote_sent'], ['Negotiation', 'negotiation'], ['Won', 'won'],
  ]
  const maxStage = Math.max(1, ...pipelineStages.map(([, status]) => financialOpportunities.filter((item) => item.status === status).length))

  return (
    <>
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-pine-600">{dateLabel}</div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-[30px]">Good morning<span className="text-ember-500">.</span></h1>
          <p className="mt-1.5 text-sm text-slate-500">Here’s what needs attention across TermoVista today.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/tasks" className="btn-secondary"><CheckSquare2 className="h-4 w-4" />View tasks</Link>
          <Link to="/leads" className="btn-primary"><Plus className="h-4 w-4" />New lead</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(({ label, value, note, icon: Icon, colour, to }) => (
          <Link key={label} to={to} className="card group p-5 transition hover:-translate-y-0.5 hover:border-pine-200 hover:shadow-lg">
            <div className="flex items-start justify-between">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${colour}`}><Icon className="h-5 w-5" strokeWidth={1.9} /></div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-pine-600" />
            </div>
            <div className="mt-5 text-3xl font-bold tracking-tight text-slate-950">{value}</div>
            <div className="mt-1 text-sm font-semibold text-slate-700">{label}</div>
            <div className="mt-1 text-xs text-slate-400">{note}</div>
          </Link>
        ))}
      </div>

      <section className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3" aria-label="Opportunity revenue summaries">
        {(['latest', 'active', 'accepted'] as const).map((levelKey) => {
          const summary = aggregateOpportunityRevenue(financialOpportunities, levelKey)
          return <article key={levelKey} className="card p-5">
            <h2 className="text-sm font-bold text-slate-700">{opportunityRevenueLevels[levelKey].label}</h2>
            <div className="mt-3 text-2xl font-bold tabular-nums text-slate-950">{formatMoney(summary.revenue)}</div>
            <div className="mt-1 text-xs text-slate-500">VAT-exclusive revenue</div>
            <div className="mt-4 flex flex-wrap justify-between gap-3 border-t border-slate-100 pt-3 text-sm">
              <span><span className="block text-xs text-slate-400">Direct-cost profit</span><span className="font-semibold tabular-nums">{formatMoney(summary.profit)}</span></span>
              <span className="text-right"><span className="block text-xs text-slate-400">Margin</span><span className="font-semibold tabular-nums">{formatPercent(summary.marginPct)}</span></span>
            </div>
            <p className="mt-3 text-xs text-slate-400">{summary.opportunityCount} opportunities with reported revenue{summary.missingProfitCount > 0 ? ' · profit and margin unavailable for incomplete source data' : ''}</p>
          </article>
        })}
      </section>

      {(overdueTasks.length > 0 || openIssues.length > 0) && (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div><div className="text-sm font-bold text-amber-900">Attention needed</div><div className="mt-0.5 text-sm text-amber-800/75">{overdueTasks.length} overdue {overdueTasks.length === 1 ? 'task' : 'tasks'} and {openIssues.length} open {openIssues.length === 1 ? 'issue' : 'issues'} need review.</div></div>
          </div>
          <Link to="/tasks" className="shrink-0 text-sm font-bold text-amber-800 hover:text-amber-950">Review priorities →</Link>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <section className="card xl:col-span-7">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div><h2 className="font-bold text-slate-900">Opportunity pipeline</h2><p className="mt-0.5 text-sm text-slate-500">Active and won work · Accepted → Active offer → Latest quoted per Opportunity; excludes lost and archived</p></div>
            <Link to="/opportunities" className="text-xs font-bold text-pine-700 hover:text-pine-900">View pipeline</Link>
          </div>
          <div className="space-y-4 p-5">
            {pipelineStages.map(([label, status]) => {
              const count = financialOpportunities.filter((item) => item.status === status).length
              const stageRecords = financialOpportunities.filter((item) => item.status === status)
              const aggregate = aggregateCurrentOpportunityRevenue(stageRecords)
              return (
                <div key={status} className="grid grid-cols-[110px_1fr_28px] items-center gap-3 sm:grid-cols-[130px_1fr_180px_28px]">
                  <span className="truncate text-xs font-semibold text-slate-600">{label}</span>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-pine-700 to-pine-400" style={{ width: `${Math.max(count ? 8 : 0, count / maxStage * 100)}%` }} /></div>
                  <span className="hidden text-right text-xs font-semibold text-slate-500 sm:block">{formatMoney(aggregate.revenue)}<span className="block text-[10px]">{formatMoney(aggregate.profit)} direct-cost profit · {formatPercent(aggregate.marginPct)} margin</span></span>
                  <span className="text-right text-sm font-bold text-slate-800">{count}</span>
                </div>
              )
            })}
          </div>
        </section>

        <section className="card overflow-hidden xl:col-span-5">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div><h2 className="font-bold text-slate-900">Today’s focus</h2><p className="mt-0.5 text-sm text-slate-500">Open tasks by due date</p></div>
            <Link to="/tasks" className="text-xs font-bold text-pine-700 hover:text-pine-900">All tasks</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {focusTasks.length ? focusTasks.map((task) => (
              <Link to="/tasks" key={task.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50">
                <span className={`h-2 w-2 shrink-0 rounded-full ${task.priority === 'urgent' || task.priority === 'high' ? 'bg-rose-500' : 'bg-amber-400'}`} />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-slate-800">{String(task.title || 'Untitled task')}</div><div className="mt-0.5 truncate text-xs text-slate-400">{String(task.relatedName || task.notes || 'General task')}</div></div>
                <span className={`text-xs font-semibold ${isOverdue(task.dueDate) ? 'text-rose-600' : 'text-slate-400'}`}>{isOverdue(task.dueDate) ? 'Overdue · ' : ''}{formatDate(task.dueDate, { year: undefined })}</span>
              </Link>
            )) : <div className="px-5 py-12 text-center text-sm text-slate-400">No open tasks. You’re all caught up.</div>}
          </div>
        </section>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <section className="card overflow-hidden xl:col-span-7">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div><h2 className="font-bold text-slate-900">Upcoming schedule</h2><p className="mt-0.5 text-sm text-slate-500">Calendar events and planned appointments</p></div>
            <Link to="/calendar" className="text-xs font-bold text-pine-700 hover:text-pine-900">Open calendar</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {schedule.length ? schedule.map((item) => (
              <div key={`${item.source}-${item.id}`} className="flex items-center gap-4 px-5 py-3.5">
                <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-lg bg-pine-50 leading-none text-pine-700"><span className="text-[9px] font-bold uppercase">{new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(new Date(item.start))}</span><span className="mt-0.5 text-base font-bold">{new Date(item.start).getDate()}</span></div>
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-slate-800">{item.title}</div><div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400"><span>{formatTime(item.start) || 'All day'}</span>{item.location && <><span>·</span><span className="inline-flex min-w-0 items-center gap-1 truncate"><MapPin className="h-3 w-3" />{item.location}</span></>}</div></div>
                <StatusBadge value={item.type} dot={false} />
              </div>
            )) : <div className="px-5 py-12 text-center text-sm text-slate-400">Nothing scheduled in the next two weeks.</div>}
          </div>
        </section>

        <section className="card overflow-hidden xl:col-span-5">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div><h2 className="font-bold text-slate-900">Recent issues</h2><p className="mt-0.5 text-sm text-slate-500">Latest after-sales cases</p></div>
            <Link to="/issues" className="text-xs font-bold text-pine-700 hover:text-pine-900">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {data.issues.slice(0, 4).map((issue) => (
              <Link to={`/issues/${issue.id}`} key={issue.id} className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600"><AlertTriangle className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-slate-800">{String(issue.title || 'Untitled issue')}</div><div className="mt-0.5 truncate text-xs text-slate-400">{humanize(issue.type)}</div></div>
                <StatusBadge value={issue.status} />
              </Link>
            ))}
            {!data.issues.length && <div className="px-5 py-12 text-center text-sm text-slate-400">No issues have been reported.</div>}
          </div>
        </section>
      </div>
    </>
  )
}
